import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY, getLastNoversSummaryLatestQueryParams, getLastNoversSummaryOlderQueryParams } from "../../../services/queryEndPoint";
import type { IMatchCommentary, IPlayersTeamSquad, InningScorecard } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { LIVE_MATCH_POLL_MS, isLiveInning, mergeInnings } from "./helpers";
import type { MatchInfo } from "./helpers";

export function useLiveMatch(matchId: string) {
  const match = useQuery<MatchInfo>({
    queryKey: [QUERY_KEY.getMatchInfo, matchId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getMatchInfo}/${matchId}`)).data,
    enabled: !!matchId,
    refetchInterval: (q) => (q.state.data?.matchStatus === "IN_PROGRESS" ? LIVE_MATCH_POLL_MS : false),
  });
  const isLive = match.data?.matchStatus === "IN_PROGRESS";

  const scores = useQuery<InningScorecard[]>({
    queryKey: [QUERY_KEY.getScoreByMatch, matchId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getScoreByMatch}/${matchId}`))?.data?.inningScoreCardList ?? [],
    enabled: !!matchId,
    refetchInterval: isLive ? LIVE_MATCH_POLL_MS : false,
  });

  const innings = useMemo(() => mergeInnings(match.data, scores.data), [match.data, scores.data]);
  const liveIndex = useMemo(() => {
    const i = innings.findIndex(isLiveInning);
    return i >= 0 ? i : Math.max(0, innings.length - 1);
  }, [innings]);

  const teamOneSnap = match.data?.teamOne?.teamSnapshotId;
  const teamTwoSnap = match.data?.teamTwo?.teamSnapshotId;
  const squad = (snap?: string) => ({
    queryKey: [QUERY_KEY.getTeamSquadForMatch, matchId, snap],
    queryFn: async () => ((await Api.get(`${QUERY_KEY.getTeamSquadForMatch}/${matchId}/${snap}`))?.data ?? { playingSquad: [], bench: [] }) as IPlayersTeamSquad,
    enabled: !!matchId && !!snap,
    staleTime: 60_000,
  });
  const squadOne = useQuery<IPlayersTeamSquad>(squad(teamOneSnap));
  const squadTwo = useQuery<IPlayersTeamSquad>(squad(teamTwoSnap));

  return { match, scores, innings, liveIndex, isLive, squadOne, squadTwo };
}

/** Over-by-over commentary for one innings (newest 5 overs first, older on demand). */
export function useInningCommentary(matchId: string, inningId: string | undefined, live: boolean, enabled = true) {
  const q = useInfiniteQuery({
    queryKey: [QUERY_KEY.getCommentary, "ml-infinite", matchId, inningId],
    initialPageParam: "latest" as "latest" | number,
    queryFn: async ({ pageParam }) => {
      const params = pageParam === "latest" ? { inningId, ...getLastNoversSummaryLatestQueryParams } : { inningId, ...getLastNoversSummaryOlderQueryParams(pageParam) };
      const res = await Api.get(`${QUERY_KEY.getCommentary}`, { params });
      return (res?.data?.overSummaryList ?? []) as IMatchCommentary[];
    },
    getNextPageParam: (lastPage, _all, lastParam) => {
      const nums = (lastPage ?? []).map((o) => o.overNumber).filter((n): n is number => typeof n === "number" && !Number.isNaN(n));
      if (!nums.length) return undefined;
      const min = Math.min(...nums);
      if (min < 1) return undefined;
      const next = min - 1;
      return typeof lastParam === "number" && lastParam === next ? undefined : next;
    },
    enabled: enabled && !!matchId && !!inningId,
    staleTime: live ? LIVE_MATCH_POLL_MS : 60_000,
    refetchInterval: live ? LIVE_MATCH_POLL_MS : false,
  });

  /** Newest over first. */
  const overs = useMemo(() => {
    const map = new Map<number, IMatchCommentary>();
    q.data?.pages.forEach((p) => p?.forEach((o) => typeof o?.overNumber === "number" && map.set(o.overNumber, o)));
    return [...map.values()].sort((a, b) => b.overNumber - a.overNumber);
  }, [q.data]);

  return { overs, isLoading: q.isLoading, isError: q.isError, hasNextPage: !!q.hasNextPage, isFetchingNextPage: q.isFetchingNextPage, fetchNextPage: () => void q.fetchNextPage() };
}
